import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaList, FaSearch, FaTh, FaTimes } from "react-icons/fa";
import { LuChevronRight, LuFactory, LuFileText, LuGavel, LuUsers } from "react-icons/lu";
import HubHomeFooter from "../hub/HubHomeFooter";
import MarketplaceAdSection from "./MarketplaceAdSection";
import MarketplaceProductGridCard from "./MarketplaceProductGridCard";
import MarketplaceProductListRow from "./MarketplaceProductListRow";
import ProductMarketplaceFiltersPanel from "./ProductMarketplaceFiltersPanel";
import { goToBuyerRfqPost, goToSupplierRegistration } from "../../utils/buyerRfqRedirect";
import { buildMarketplaceModuleTabs } from "../../constants/marketplaceModuleTabs";
import { useShowListAsSupplierButton } from "../../hooks/useShowListAsSupplierButton";
import "../../assets/styles/explore_product_categories.scss";
import "../../assets/styles/product_marketplace.scss";

const PRODUCT_SORT_OPTIONS = [
  { value: "best_match", label: "Best Match", verifiedOnly: false },
  { value: "newest", label: "Verified First", verifiedOnly: true },
  { value: "price_asc", label: "Price: Low to High", verifiedOnly: false },
  { value: "moq_asc", label: "MOQ: Low to High", verifiedOnly: false },
  { value: "delivery", label: "Fastest Delivery", verifiedOnly: false },
  { value: "rated", label: "Highest Rated", verifiedOnly: false },
  { value: "recent", label: "Recently Added", verifiedOnly: false },
];

function formatStatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

function subcategoryProductDescription(name) {
  return `Source ${name} from verified manufacturers and distributors. Compare specs, request quotes, and connect with trusted B2B suppliers across India and export markets.`;
}

function searchProductDescription(query) {
  return `Browse B2B products matching "${query}". Compare specs, request quotes, and connect with verified suppliers across India and export markets.`;
}

function SubcategoryProductHeroStats({ subCategory }) {
  const suppliers = subCategory?.supplier_count ?? 0;
  const buyerReqs = subCategory?.buyer_request_count ?? 0;
  const auctions = subCategory?.auction_count ?? 0;
  const cmOptions = Math.max(Math.round(suppliers / 79), 8);

  const pills = [
    { value: suppliers, label: "verified suppliers", icon: LuUsers, iconClass: "explore-subcat-products-hero__stat-icon--suppliers" },
    { value: buyerReqs, label: "active buyer reqs", icon: LuFileText, iconClass: "explore-subcat-products-hero__stat-icon--buyer-reqs" },
    { value: auctions, label: "live auctions", icon: LuGavel, iconClass: "explore-subcat-products-hero__stat-icon--auctions" },
    { value: cmOptions, label: "support custom mfg", icon: LuFactory, iconClass: "explore-subcat-products-hero__stat-icon--cm" },
  ];

  return (
    <div className="explore-subcat-products-hero__stats">
      {pills.map((pill) => {
        const Icon = pill.icon;
        return (
          <div key={pill.label} className="explore-subcat-products-hero__stat-pill">
            <div className={`explore-subcat-products-hero__stat-icon ${pill.iconClass}`}>
              <Icon aria-hidden />
            </div>
            <span className="explore-subcat-products-hero__stat-text">
              <strong>{formatStatCount(pill.value)}</strong> {pill.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ExploreSubcategoryProductsBrowse({
  parentCategory,
  subCategory,
  subSubCategories = [],
  products = [],
  loading = false,
  applied,
  pagination = {},
  pageSize,
  pageNumbers = [],
  draft,
  setDraft,
  categoryOptions = [],
  subCategoryOptions = [],
  subSubCategoryOptions = [],
  brands = [],
  locations = [],
  onApplyFilters,
  onClearFilters,
  onSearchSubmit,
  onSortChange,
  onGoToPage,
  onChangePageSize,
  onToggleCompare,
  compareIds = [],
  subcategoryProductBanners = [],
}) {
  const navigate = useNavigate();
  const showListAsSupplier = useShowListAsSupplierButton();
  const [localSearch, setLocalSearch] = useState(applied.q || "");
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    setLocalSearch(applied.q || "");
  }, [applied.q]);

  const trimmedSearch = String(applied.q || "").trim();
  const isSearchMode = !subCategory && Boolean(trimmedSearch);
  const subCategoryName = subCategory?.name || subCategory?.sub_category || "Products";
  const pageTitle = isSearchMode ? trimmedSearch : subCategoryName;
  const pageDescription = isSearchMode
    ? searchProductDescription(trimmedSearch)
    : subcategoryProductDescription(subCategoryName);
  const parentName = parentCategory?.name || "";
  const parentCategoryId = parentCategory?.id ?? parentCategory?.category_id;
  const showingTotal = pagination.total || products.length || 0;

  const heroStatsSource = useMemo(() => {
    if (subCategory) return subCategory;
    const supplierIds = new Set(products.map((product) => product.company_id).filter(Boolean));
    return {
      supplier_count: supplierIds.size,
      buyer_request_count: 0,
      auction_count: 0,
      product_count: showingTotal,
    };
  }, [subCategory, products, showingTotal]);

  const moduleTabs = useMemo(
    () =>
      buildMarketplaceModuleTabs({
        activeKey: "products",
        categoryId: parentCategoryId,
        subCategoryId: subCategory?.id ?? subCategory?.sub_category_id,
        counts: {
          products: showingTotal || heroStatsSource?.product_count || 0,
          buyerReqs: heroStatsSource?.buyer_request_count || 0,
          auctions: heroStatsSource?.auction_count || 0,
          cm: heroStatsSource?.cm_count || 0,
        },
      }),
    [heroStatsSource, showingTotal, parentCategoryId, subCategory],
  );

  const categoryBrowseHref = parentCategoryId
    ? `/marketplace/products?category=${encodeURIComponent(parentCategoryId)}`
    : "/marketplace/products";

  return (
    <>
      <section className="explore-subcat-products-hero">
        <div className="explore-subcat-products-hero__inner">
          <div className="explore-subcat-products-hero__breadcrumb-row">
            <nav className="explore-subcat-products-hero__breadcrumb" aria-label="Breadcrumb">
              <Link to="/home">Home</Link>
              <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
              <Link to="/marketplace/products">Categories</Link>
              {parentName ? (
                <>
                  <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                  <Link to={categoryBrowseHref}>{parentName}</Link>
                </>
              ) : null}
              {isSearchMode ? (
                <>
                  <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                  <span className="is-current">Search: {trimmedSearch}</span>
                </>
              ) : subCategoryName ? (
                <>
                  <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                  <span className="is-current">{subCategoryName}</span>
                </>
              ) : null}
            </nav>
            <button
              type="button"
              className="explore-subcat-products-hero__back"
              onClick={() => navigate(categoryBrowseHref)}
            >
              <FaChevronLeft aria-hidden /> Back
            </button>
          </div>

          <div className="explore-subcat-products-hero__main">
            <div className="explore-subcat-products-hero__intro">
              <h1>{pageTitle}</h1>
              <p>{pageDescription}</p>
            </div>
            <SubcategoryProductHeroStats subCategory={heroStatsSource} />
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-tabs-bar">
        <div className="explore-subcat-products-tabs-bar__inner">
          <nav className="explore-subcat-products-tabs" aria-label="Listing types">
            {moduleTabs.map((tab) =>
              tab.active ? (
                <span key={tab.key} className="explore-subcat-products-tabs__item is-active">
                  {tab.label}
                </span>
              ) : (
                <Link key={tab.key} to={tab.href} className="explore-subcat-products-tabs__item">
                  {tab.label}
                </Link>
              ),
            )}
          </nav>
        </div>
      </div>

      <section className="explore-subcat-products-controls">
        <div className="explore-subcat-products-controls__inner">
          <div className="explore-subcat-products-controls__top-row">
            <form
              className="explore-subcat-products-controls__search"
              onSubmit={(e) => {
                e.preventDefault();
                onSearchSubmit?.(localSearch);
              }}
            >
              <FaSearch aria-hidden />
              <input
                type="search"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={
                  isSearchMode
                    ? "Refine search — product, supplier, spec, material, capacity…"
                    : `Search ${subCategoryName.toLowerCase()} type, supplier, spec, material, capacity…`
                }
                aria-label={isSearchMode ? "Search products" : `Search ${subCategoryName}`}
              />
              {localSearch ? (
                <button
                  type="button"
                  className="explore-subcat-products-controls__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setLocalSearch("");
                    onSearchSubmit?.("");
                  }}
                >
                  <FaTimes aria-hidden />
                </button>
              ) : null}
            </form>

            <div className="explore-subcat-products-controls__sort" role="group" aria-label="Sort products">
              {PRODUCT_SORT_OPTIONS.map((opt) => {
                const effectiveSort =
                  !applied.sort || applied.sort === "newest" ? "best_match" : applied.sort;
                const isActive = opt.verifiedOnly
                  ? applied.verifiedOnly === "1"
                  : applied.verifiedOnly !== "1" && effectiveSort === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    className={`explore-subcat-products-controls__sort-pill${isActive ? " is-active" : ""}`}
                    aria-pressed={isActive}
                    onClick={() => onSortChange?.(opt)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="explore-subcat-products-controls__actions">
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--orange"
              onClick={() => goToBuyerRfqPost(navigate)}
            >
              Post Buying Requirement
            </button>
            {showListAsSupplier ? (
              <button
                type="button"
                className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--navy"
                onClick={() => goToSupplierRegistration(navigate)}
              >
                List as Supplier
              </button>
            ) : null}
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-main">
        <ProductMarketplaceFiltersPanel
          draft={draft}
          setDraft={setDraft}
          categoryOptions={categoryOptions}
          subCategoryOptions={subCategoryOptions}
          subSubCategoryOptions={subSubCategoryOptions}
          locations={locations}
          brands={brands}
          onApply={onApplyFilters}
          onClear={onClearFilters}
        />

        <section className="explore-subcat-products-content">
          <div className="explore-subcat-products-content__head">
            <p className="explore-subcat-products-content__count">
              {showingTotal > 0
                ? isSearchMode
                  ? `${showingTotal.toLocaleString()} result${showingTotal === 1 ? "" : "s"} for "${trimmedSearch}" — Products`
                  : `${showingTotal.toLocaleString()} result${showingTotal === 1 ? "" : "s"} in ${subCategoryName} — Products`
                : isSearchMode
                  ? `0 results for "${trimmedSearch}" — Products`
                  : `0 results in ${subCategoryName} — Products`}
            </p>
            <div className="explore-subcat-products-view-toggle" aria-label="View mode">
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "list" ? " is-active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                aria-label="Horizontal list view"
                title="Horizontal list view"
              >
                <FaList aria-hidden />
              </button>
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "grid" ? " is-active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-pressed={viewMode === "grid"}
                aria-label="Vertical grid view"
                title="Vertical grid view"
              >
                <FaTh aria-hidden />
              </button>
            </div>
          </div>

          <div className={`explore-subcat-products-list explore-subcat-products-list--${viewMode}`}>
            {loading && products.length === 0
              ? Array.from({ length: Math.min(pageSize, viewMode === "grid" ? 8 : 6) }).map((_, idx) => (
                  <div
                    key={idx}
                    className={
                      viewMode === "grid"
                        ? "marketplace-product-grid-card marketplace-product-grid-card--skeleton"
                        : "marketplace-product-row marketplace-product-row--skeleton"
                    }
                    aria-hidden
                  />
                ))
              : null}
            {!loading || products.length
              ? products.map((item) =>
                  viewMode === "grid" ? (
                    <MarketplaceProductGridCard
                      key={item.id}
                      item={item}
                      onToggleCompare={onToggleCompare}
                      compared={compareIds.includes(item.id)}
                    />
                  ) : (
                    <MarketplaceProductListRow
                      key={item.id}
                      item={item}
                      onToggleCompare={onToggleCompare}
                      compared={compareIds.includes(item.id)}
                    />
                  ),
                )
              : null}
            {!loading && products.length === 0 ? (
              <p className="explore-categories-empty">No products match your filters yet.</p>
            ) : null}
          </div>

          {pagination.total_pages > 0 ? (
            <div className="product-marketplace-pagination-wrap">
              <nav className="product-marketplace-pagination" aria-label="Pagination">
                <button
                  type="button"
                  disabled={Number(applied.page) <= 1}
                  onClick={() => onGoToPage?.(Number(applied.page) - 1)}
                >
                  <FaChevronLeft aria-hidden /> Previous
                </button>
                {pageNumbers.map((page, idx) =>
                  page === "…" ? (
                    <span key={`ellipsis-${idx}`} className="product-marketplace-pagination__ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={Number(applied.page) === page ? "is-active" : ""}
                      onClick={() => onGoToPage?.(page)}
                    >
                      {page}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={Number(applied.page) >= pagination.total_pages}
                  onClick={() => onGoToPage?.(Number(applied.page) + 1)}
                >
                  Next <FaChevronRight aria-hidden />
                </button>
              </nav>
              <label className="product-marketplace-page-size">
                Show
                <select
                  value={pageSize}
                  onChange={(e) => onChangePageSize?.(Number(e.target.value))}
                  aria-label="Products per page"
                >
                  {[12, 24, 36, 48].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                per page
              </label>
            </div>
          ) : null}
        </section>
      </div>

      <MarketplaceAdSection
        banners={subcategoryProductBanners}
        ariaLabel="Marketplace subcategory product promotional banners"
        hideFallback
      />

      <HubHomeFooter />
    </>
  );
}
